import { DocumentReader } from '../../word/api/DocumentReader';
import { applyDocumentRestructureOps } from '../../word/api/DocumentRestructurer';
import { log, logError } from '../../utils/logging';
import { RedlineInserter } from '../../word/trackChanges/RedlineInserter';
import { computeWordDiff } from '../../word/utils/TextDiff';
import { applyViaHybridEngine } from '../hybridApply/hybridEngine';
import { isLegacyApplyEnabled } from '../../utils/addInPreferences';
import { normalizeWordParagraphBaseline } from '../../utils/bidiText';
import { spliceSnippetIntoParagraph } from '../../utils/locateSnippetInParagraph';

/* global Word */

function isWordAvailable(): boolean {
  return typeof Word !== 'undefined' && typeof Word.run === 'function';
}

/**
 * Word API adapter for proofread operations.
 * Handles navigation, highlighting, and text replacement.
 */
export class ProofreadWordAdapter {
  private static escapeHtml(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Build word-level track-changes HTML for the UI preview and for RedlineInserter.
   *
   * Uses LCS-based word diff so only truly changed words are wrapped in <del>/<ins>;
   * unchanged words are emitted as plain text.  This matches what the Editor panel
   * shows when OOXML track-changes are converted via TrackChangesParser.
   *
   * Apply path: RedlineInserter parses this HTML into Range.search ops; uses
   * whole-paragraph replace only when incremental search would exceed Word.js limits.
   */
  static buildTrackChangesHtml(originalText: string, suggestion: string): string {
    const ops = computeWordDiff(originalText, suggestion);
    let html = '';
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const text = this.escapeHtml(op.words.join(' '));
      const needsTrailingSpace = i < ops.length - 1;
      const spaceSuffix = needsTrailingSpace ? ' ' : '';
      if (op.type === 'equal') {
        html += text + spaceSuffix;
      } else if (op.type === 'delete') {
        html += `<del class="track-change-deletion track-change-new">${text}</del>${spaceSuffix}`;
      } else {
        html += `<ins class="track-change-insertion track-change-new">${text}</ins>${spaceSuffix}`;
      }
    }
    return html;
  }

  /**
   * Navigate to the paragraph at the given index in the document.
   */
  static async navigateToParagraph(paragraphIndex: number): Promise<void> {
    await DocumentReader.navigateToParagraph(paragraphIndex);
  }

  /**
   * Navigate to a paragraph by searching for the given text content.
   */
  static async navigateByContent(text: string): Promise<void> {
    await DocumentReader.navigateToParagraphByContent(text);
  }

  /**
   * Select the target clause in Word before applying, same as MCC Submit-to-Response.
   * Prefer a resolved body index; fall back to content search.
   */
  static async focusClauseBeforeApply(
    paragraphIndex?: number | null,
    searchText?: string
  ): Promise<void> {
    if (paragraphIndex != null && !Number.isNaN(Number(paragraphIndex)) && Number(paragraphIndex) >= 0) {
      await this.navigateToParagraph(Number(paragraphIndex));
      return;
    }
    if (searchText) {
      await this.navigateByContent(searchText);
    }
  }

  /**
   * Read the live paragraph text and splice `snippetSuggestion` in place of
   * `snippetOriginal`, so the hybrid engine can diff a full-paragraph target and
   * only touch the intended snippet. Returns null when the snippet cannot be
   * located — caller MUST abort (never pass a snippet-only target to hybrid).
   *
   * Uses the same `normalizeWordParagraphBaseline` as `applyViaHybridEngine`, so
   * the composed target lines up 1:1 with the hybrid baseline (minimal ops).
   */
  static async composeParagraphTargetFromSnippet(
    paragraphIndex: number,
    snippetOriginal: string,
    snippetSuggestion: string,
  ): Promise<string | null> {
    if (!isWordAvailable()) return null;
    try {
      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();
        const rawParagraph = paragraphs.items[paragraphIndex];
        if (!rawParagraph) return null;
        const paragraph = rawParagraph as unknown as Word.Paragraph;
        const whole = paragraph.getRange('Whole');
        whole.load('text');
        await context.sync();
        const baseline = normalizeWordParagraphBaseline(whole.text || '');
        if (!baseline) return null;

        // Exact indexOf is not enough: resolveParagraphForApply already matched
        // via normalizeForMatch (strips Word auto-numbers like `1)`, collapses
        // whitespace / RTL marks). Reuse that locate logic so hybrid compose
        // succeeds on the same paragraphs search found.
        return spliceSnippetIntoParagraph(baseline, snippetOriginal, snippetSuggestion);
      });
    } catch (err) {
      logError('[ProofreadWordAdapter] composeParagraphTargetFromSnippet failed', err);
      return null;
    }
  }

  /**
   * Replace the first occurrence of originalText in the document with suggestion.
   * Uses Word search API so no selection pre-selection is needed.
   * Returns true on success.
   */
  static async replaceText(
    originalText: string,
    suggestion: string,
    paragraphIndex?: number
  ): Promise<boolean> {
    if (!originalText || !suggestion || !isWordAvailable()) return false;
    try {
      // Same routing as MCC/CL Client Request and Editor Replace: default to the hybrid
      // engine (Profile "Legacy apply" toggle opts back to the legacy ladder). Apply /
      // Apply all in Agent mode must respect this toggle across the add-in.
      const useHybridApply = !isLegacyApplyEnabled();
      if (useHybridApply) {
        let resolvedIndex: number | null =
          paragraphIndex != null && paragraphIndex >= 0 ? paragraphIndex : null;
        const resolved = await DocumentReader.resolveParagraphForApply(
          resolvedIndex ?? undefined,
          { searchText: originalText },
        );
        if (resolved != null) resolvedIndex = resolved;
        if (resolvedIndex != null && resolvedIndex >= 0) {
          await this.focusClauseBeforeApply(resolvedIndex, originalText);
          // originalText/suggestion here are SNIPPETS inside the paragraph (Agent
          // Suggestions and Proofread show a snippet, not the full clause). The
          // hybrid engine diffs the whole paragraph baseline against `cleanTarget`,
          // so passing only the snippet as target would delete everything else in
          // the paragraph. Compose a full-paragraph target by splicing `suggestion`
          // into the live paragraph in place of `originalText`. MCC/CL Client
          // Request Submit and Editor Replace already pass full-clause targets to
          // `applyViaHybridEngine` directly, so they are unaffected.
          const composedTarget = await this.composeParagraphTargetFromSnippet(
            Number(resolvedIndex),
            originalText,
            suggestion,
          );
          if (composedTarget == null) {
            log('[ProofreadWordAdapter] replaceText: snippet not found in paragraph, aborting hybrid', {
              paragraphIndex: resolvedIndex,
              originalSample: originalText.slice(0, 80),
            });
            return false;
          }
          const hybrid = await applyViaHybridEngine(Number(resolvedIndex), composedTarget);
          log('[ProofreadWordAdapter] replaceText: hybrid engine', {
            paragraphIndex: resolvedIndex,
            applied: hybrid.applied,
            reason: hybrid.reason ?? null,
          });
          if (hybrid.applied) return true;
          // Hybrid miss is a clean no-op — surface as failure to the caller (same as
          // MCC/CL Submit-to-Response). Do NOT silently fall through to legacy.
          return false;
        }
        log('[ProofreadWordAdapter] replaceText: hybrid skipped, no paragraph index', {
          paragraphIndex: paragraphIndex ?? null,
        });
        return false;
      }

      await this.focusClauseBeforeApply(paragraphIndex, originalText);
      const trackChangesHtml = this.buildTrackChangesHtml(originalText, suggestion);
      const result = await RedlineInserter.applyWordLevelChangesIncrementally(
        originalText,
        suggestion,
        trackChangesHtml,
        paragraphIndex
      );
      if (result.applied) return true;

      log('[ProofreadWordAdapter] replaceText: incremental failed, trying applySubmitAsIs', {
        paragraphIndex: paragraphIndex ?? null,
      });
      const submitResult = await RedlineInserter.applySubmitAsIs(
        originalText,
        suggestion,
        paragraphIndex
      );
      if (submitResult.applied) return true;

      if (paragraphIndex != null && paragraphIndex >= 0) {
        log('[ProofreadWordAdapter] replaceText: applySubmitAsIs failed, trying replace_text op', {
          paragraphIndex,
        });
        const restructureResult = await applyDocumentRestructureOps([
          { op: 'replace_text', agent_paragraph_index: paragraphIndex, text: suggestion },
        ]);
        if (restructureResult.ok && restructureResult.applied > 0) return true;
      }

      return false;
    } catch (err) {
      logError('[ProofreadWordAdapter] replaceText failed', err);
      return false;
    }
  }

  /**
   * Apply all replacements in top-to-bottom order.
   * Returns counts of succeeded and failed replacements.
   */
  static async replaceAll(
    issues: Array<{ originalText: string; suggestion: string; paragraphIndex?: number }>
  ): Promise<{ succeeded: number; failed: number }> {
    let succeeded = 0;
    let failed = 0;
    for (const issue of issues) {
      const ok = await this.replaceText(issue.originalText, issue.suggestion, issue.paragraphIndex);
      if (ok) succeeded++;
      else failed++;
    }
    return { succeeded, failed };
  }

  /**
   * Analyze current document for defined terms.
   * Returns a map: term -> list of defining paragraph occurrences.
   */
  static extractDefinedTermsFromParagraphs(
    paragraphs: Array<{ index: number; text: string }>
  ): Map<string, Array<{ paragraphIndex: number; snippet: string }>> {
    const defined = new Map<string, Array<{ paragraphIndex: number; snippet: string }>>();
    // Matches: "Term" means / "Term" shall mean / "Term" is defined / "Term" has the meaning
    const definePattern = /[\u201C\u201D""«»\u05F4]([A-Z\u05D0-\u05EA][A-Za-z\u05D0-\u05EA\s\-']{1,60}?)[\u201C\u201D""«»\u05F4]\s*(?:means|shall mean|is defined|refers to|has the meaning)/gi;

    for (const p of paragraphs) {
      let match: RegExpExecArray | null;
      while ((match = definePattern.exec(p.text)) !== null) {
        const term = match[1].trim();
        const arr = defined.get(term) || [];
        arr.push({ paragraphIndex: p.index, snippet: p.text.substring(0, 120) });
        defined.set(term, arr);
      }
    }
    return defined;
  }

  /**
   * Find terms defined once that are never referenced in the rest of the document.
   */
  static findUnusedTerms(
    defined: Map<string, Array<{ paragraphIndex: number; snippet: string }>>,
    paragraphs: Array<{ index: number; text: string }>
  ): string[] {
    const fullText = paragraphs.map((p) => p.text).join('\n');
    const unused: string[] = [];

    for (const [term, occurrences] of defined.entries()) {
      if (occurrences.length !== 1) continue; // Multiple definitions are handled separately
      // Term should appear at least twice in full text (once as definition, once as usage)
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const usageCount = (fullText.match(new RegExp(escapedTerm, 'gi')) || []).length;
      if (usageCount <= 1) {
        unused.push(term);
      }
    }
    return unused;
  }

  /**
   * Scan paragraphs for common legal placeholder patterns.
   */
  static findPlaceholders(
    paragraphs: Array<{ index: number; text: string }>
  ): Array<{ paragraphIndex: number; text: string }> {
    const results: Array<{ paragraphIndex: number; text: string }> = [];
    const PATTERNS = [
      /\[(?:INSERT|TBD|DATE|NAME|AMOUNT|NUMBER|ADDRESS|FILL|PARTY|TERM|\s*_+\s*)[^\]]{0,120}\]/gi,
      /\[\[[^\]]{0,120}\]\]/g,
      /_{4,}/g,
      /\(\s*\[+\s*\]+\s*\)/g,
    ];

    for (const p of paragraphs) {
      for (const pattern of PATTERNS) {
        pattern.lastIndex = 0;
        let m: RegExpExecArray | null;
        while ((m = pattern.exec(p.text)) !== null) {
          results.push({ paragraphIndex: p.index, text: m[0] });
        }
      }
    }
    return results;
  }

  /**
   * Fetch active (non-resolved) Word document comments using the Office.js Comments API.
   * Returns author, comment body text, and the anchor text (highlighted text in the document).
   */
  static async getWordComments(): Promise<Array<{
    id: string;
    author: string;
    text: string;
    anchorText: string;
  }>> {
    if (!isWordAvailable()) return [];
    try {
      return await Word.run(async (context) => {
        // Use `any` casts — Office.js Comment API shape varies across API sets
        const body = context.document.body as any;
        const commentsCollection = body.getComments();
        commentsCollection.load('items');
        await context.sync();

        const items: any[] = commentsCollection.items ?? [];
        if (!items.length) return [];

        // Stage 1: load metadata, content collection, and anchor range
        const anchorRanges: Word.Range[] = [];
        for (const c of items) {
          c.load(['id', 'authorName', 'resolved', 'isResolved']);
          if (c.content?.load) c.content.load('items');
          const r: Word.Range = c.getRange();
          r.load('text');
          anchorRanges.push(r);
        }
        await context.sync();

        // Stage 2: load content paragraph texts
        for (const c of items) {
          const contentItems: any[] = c.content?.items ?? [];
          for (const ci of contentItems) {
            if (ci?.load) ci.load('text');
          }
        }
        await context.sync();

        const results: Array<{ id: string; author: string; text: string; anchorText: string }> = [];
        for (let i = 0; i < items.length; i++) {
          const c = items[i];
          // Support both resolved (Word API 1.4) and isResolved (older)
          const isResolved = c.resolved ?? c.isResolved ?? false;
          if (isResolved) continue;

          const contentItems: any[] = c.content?.items ?? [];
          const commentText = contentItems
            .map((ci: any) => ci?.text ?? '')
            .join(' ')
            .trim();

          results.push({
            id: c.id || `wc-${i}`,
            author: c.authorName || '',
            text: commentText,
            anchorText: anchorRanges[i].text || '',
          });
        }
        log('[ProofreadWordAdapter] getWordComments:done', { count: results.length });
        return results;
      });
    } catch (err) {
      logError('[ProofreadWordAdapter] getWordComments failed', err);
      return [];
    }
  }
}
